import { Button } from "./Button";
import { useRedirectToPaymentPage } from "@/hooks/payment";
import React from "react";
import {
  ClipboardList,
  Layers,
  Clock,
  Play,
  Eye,
  RotateCcw,
} from "lucide-react";

interface InfoCardProps {
  title: string;
  description: string;
  imgSrc: string;
}

export function InfoCard({ title, description, imgSrc }: InfoCardProps) {
  return (
    <section className="max-w-[263px] rounded-2xl bg-white px-4 py-6 text-center capitalize shadow-md transition-all duration-300 ease-in hover:-translate-y-1 hover:scale-105 hover:shadow-xl xl:px-5 xl:py-8">
      <div className="mb-4 inline-flex h-20 w-20 items-center justify-center rounded-full border-4 border-[#FF9053] p-2 sm:h-24 sm:w-24">
        <img className="h-[90%]" src={imgSrc} alt={title} />
      </div>
      <h3 className="text-card-base-0 mb-2 font-[600] text-[#1B438F]">
        {title}
      </h3>
      <p className="text-card-xs-0 leading-relaxed font-[300] text-balance text-[#626262]">
        {description}
      </p>
    </section>
  );
}

interface CardProps {
  children: React.ReactNode;
}

export function Card({ children }: CardProps) {
  return (
    <article className="inline-block rounded-3xl border border-[#E7ECF4] bg-white px-6 py-8 text-lg font-[400] shadow-xl shadow-[#1B438F1A] sm:px-10 sm:py-12 md:rounded-3xl md:px-24 md:py-16">
      {children}
    </article>
  );
}

export function CourseFeaturesCard() {
  const features = [
    "30 Beginner Videos for Quant",
    "60+ Hours of Pre-Recorded Classes (Quant/Verbal)",
    "Full Study Material (1000+ Questions)",
    "2 Full-Length Adaptive Mock Tests",
    "1200-Word Vocabulary List",
    "1-Month and 3-Month Study Plans",
    "Validity of 3 Months (Extendable)",
  ];

  const { redirectToPaymentPage } = useRedirectToPaymentPage();

  return (
    <Card>
      <h2 className="text-xl-0 mb-3 text-center leading-tight font-[900] text-[#1B438F] uppercase">
        GRE Universe Course
      </h2>
      <p className="text-sm-0 sm:text-x text-center font-[600] text-pretty capitalize">
        Your Entire GRE Universe In One Course — Everything You Need, Nothing
        You Don't.
      </p>

      <ul className="my-10">
        {features.map((feature, i) => (
          <li
            key={i}
            className="flex items-center gap-3 rounded border-b border-b-[#E7ECF4] px-2 py-1.5 transition-colors last:border-b-0 hover:bg-[#F9FAFB]"
          >
            <div className="min-h-3 min-w-3 flex-shrink-0 rounded-full border-2 border-[#FF9053] bg-[#FF90534D]"></div>
            <p className="text-sm-0 font-[500] text-[#211C37]">{feature}</p>
          </li>
        ))}
      </ul>

      <div className="text-sm-0 text-white">
        <Button
          onClick={() => {
            redirectToPaymentPage();
          }}
          label="Enroll now"
        />
      </div>
    </Card>
  );
}

interface TestimonialCardProps {
  title: string;
  description: string;
  greScore: number | string;
  name: string;
  linkedin: string;
  designation: string;
  imgSrc: string;
}

export function TestimonialCard({
  title,
  description,
  greScore,
  name,
  designation,
  linkedin,
  imgSrc,
}: TestimonialCardProps) {
  return (
    <article className="isolate mt-2 flex w-[90%] max-w-4xl flex-col items-center gap-5 rounded-2xl border border-[#E7ECF4] bg-white px-6 py-6 shadow-lg shadow-[#1B438F0D] transition-all duration-300 ease-in hover:scale-105 hover:shadow-2xl hover:shadow-[#1B438F15] sm:flex-row sm:items-stretch sm:gap-8 sm:px-8 sm:py-7 md:px-14 md:py-10 lg:gap-10">
      <div className="relative flex flex-shrink-0 justify-center sm:w-40 sm:justify-start md:w-48">
        <img
          loading="lazy"
          className="absolute -top-1 -left-1 -z-10 h-6 w-6 sm:-top-3 sm:-left-3 sm:h-10 sm:w-10"
          src="/icons/quote.png"
          alt="quote"
        />
        <img
          loading="lazy"
          className="h-32 w-32 rounded-full object-cover ring-4 ring-[#FF9053] sm:h-40 sm:w-40 md:h-48 md:w-48"
          src={imgSrc}
          alt={name}
        />
      </div>
      <section className="flex min-w-0 flex-grow flex-col justify-between">
        <div>
          <h3 className="text-card-base-1 mb-3 line-clamp-2 font-[800]">
            {title}
          </h3>
          <p className="text-card-xs-n1 mb-4 line-clamp-4 font-[400] text-[#626262] italic sm:line-clamp-none">
            {description}
          </p>
        </div>
        <div>
          <div className="mb-3 flex items-center gap-4 text-[#1B438F]">
            <p className="text-card-base-2 font-[900]">
              {typeof greScore === "string" ? greScore : `${greScore}/340`}
            </p>
            <a
              className="inline-block flex-shrink-0"
              href={linkedin}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                loading="lazy"
                className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7"
                src="/icons/linkedin.svg"
                alt="LinkedIn"
              />
            </a>
          </div>
          <p className="text-card-xs-1 truncate font-[900]">{name}</p>
          <p className="truncate text-xs font-[500] text-[#108267] sm:text-sm md:text-base">
            {designation}
          </p>
        </div>
      </section>
    </article>
  );
}

interface CourseCardProps {
  title: string;
  articles: number | `${number}`;
  assignments: number | `${number}`;
  members: number | `${number}`;
  imgSrc: string;
  className: string;
}

interface SimpleCourseCardProps {
  title: string;
  imgSrc: string;
  className: string;
}

export function CourseCard({
  title,
  articles,
  assignments,
  members,
  imgSrc,
  className,
}: CourseCardProps) {
  return (
    <article
      className={`flex-grow rounded-xl px-4 pt-6 pb-4 shadow-md transition-shadow duration-300 hover:shadow-lg ${className}`}
    >
      <div className="mx-2 mb-4">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm">
          <img className="h-7 w-7" src={imgSrc} alt={title} />
        </div>
        <h4 className="text-sm font-[600] text-[#1F1D39]">{title}</h4>
      </div>
      <div className="flex items-center justify-between gap-2 rounded-lg bg-white/80 px-4 py-2.5">
        <div className="flex items-center gap-2 text-xs font-[500] text-[#626262]">
          <img src="/icons/book.png" alt="articles" className="h-4 w-4" />
          <span>{articles}</span>
        </div>
        <div className={`${className} h-5 w-px rounded-full opacity-30`}></div>
        <div className="flex items-center gap-2 text-xs font-[500] text-[#626262]">
          <img src="/icons/tasks.png" alt="assignments" className="h-4 w-4" />
          <span>{assignments}</span>
        </div>
        <div className={`${className} h-5 w-px rounded-full opacity-30`}></div>
        <div className="flex items-center gap-2 text-xs font-[500] text-[#626262]">
          <img src="/icons/people.png" alt="members" className="h-4 w-4" />
          <span>{members}</span>
        </div>
      </div>
    </article>
  );
}

export function SimpleCourseCard({
  title,
  imgSrc,
  className,
}: SimpleCourseCardProps) {
  const getActionText = () => {
    if (title === "Mock Tests") return "Take Mock";
    return `Learn ${title}`;
  };

  return (
    <div
      className={`cursor-pointer rounded-lg p-4 transition-all duration-300 hover:scale-105 hover:shadow-lg ${className} min-w-[200px] flex-1`}
    >
      <div className="mb-3 flex items-center gap-3">
        <img
          src={imgSrc || "/placeholder.svg"}
          alt={title}
          className="h-12 w-12"
        />
        <div>
          <h3 className="font-semibold text-[#211C37]">{title}</h3>
          <p className="text-xs text-[#85878D]">{getActionText()}</p>
        </div>
      </div>
    </div>
  );
}

interface MockTestCardProps {
  title: string;
  sectionsCount?: number;
  duration: string;
  status: "In progress" | "Completed" | "Not started";
  retakesUsed?: number;
  retakesTotal?: number;
  onResume?: () => void;
  onReview?: () => void;
  onRetake?: () => void;
}

export default function MockTestCard({
  title,
  sectionsCount,
  duration,
  status,
  retakesUsed = 0,
  retakesTotal = 3,
  onResume,
  onReview,
  onRetake,
}: MockTestCardProps) {

  return (
    <div className="flex w-full flex-col gap-4 rounded-md border border-gray-200 bg-white p-4 font-sans shadow-sm md:flex-row md:items-center md:justify-between">
      {/* Left Section: Info */}
      <div className="flex items-start gap-4">
        {/* Test Icon Wrapper */}
        <div className="flex items-center justify-center rounded-lg bg-sky-50 p-3 text-sky-600">
          <ClipboardList className="h-6 w-6" />
        </div>

        {/* Text Details */}
        <div className="space-y-1.5">
          <h3 className="text-base font-semibold text-gray-800">{title}</h3>

          <div className="flex flex-wrap items-center gap-2 text-xs font-medium">
            {sectionsCount && (
              <div className="flex items-center gap-1 rounded-md bg-sky-50 px-2.5 py-1 text-sky-700">
                <Layers className="h-3.5 w-3.5" />
                <span>{sectionsCount} Sections</span>
              </div>
            )}

            {/* Duration Badge */}
            <div className="flex items-center gap-1 rounded-md bg-emerald-50 px-2.5 py-1 text-emerald-700">
              <Clock className="h-3.5 w-3.5" />
              <span>{duration}</span>
            </div>

            {/* Status Indicator */}
            <span className="ml-1 font-semibold text-emerald-600">
              {status}
            </span>
          </div>
        </div>
      </div>

      {/* Right Section: Retakes & Actions */}
      <div className="flex flex-wrap items-center justify-between gap-6 border-t border-gray-100 pt-3 md:justify-end md:border-t-0 md:pt-0">
        {/* Retakes Tracker */}
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-[10px] leading-none font-medium tracking-wider text-gray-400 uppercase">
              Retakes
            </p>
            <p className="mt-0.5 text-sm font-semibold text-gray-600">
              {retakesUsed}/{retakesTotal}
            </p>
          </div>
          {/* Dot Indicators */}
          <div className="flex gap-1.5">
            {Array.from({ length: retakesTotal }).map((_, i) => (
              <span
                key={i}
                className={`h-2.5 w-2.5 rounded-full ${
                  i < retakesUsed
                    ? "bg-sky-700"
                    : "border border-gray-300 bg-gray-50"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex w-full items-center gap-2.5 sm:w-auto">
          <button
            onClick={onResume}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 sm:flex-none"
          >
            <Play className="h-4 w-4 fill-current text-gray-600" />
            Resume
          </button>

          <button
            onClick={onReview}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 sm:flex-none"
          >
            <Eye className="h-4 w-4" />
            Review
          </button>

          <button
            onClick={onRetake}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-sky-700 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-sky-800 sm:flex-none"
          >
            <RotateCcw className="h-4 w-4 stroke-[2.5]" />
            Retake
          </button>
        </div>
      </div>
    </div>
  );
}
